// Cache local (IndexedDB) do historico de mensagens por ticket — mesmo
// mecanismo ja usado nos apps EquipeChat Visita e Motoboy. Reabrir uma
// conversa ja vista antes renderiza na hora (sem esperar o servidor
// responder de novo), e so busca o que e novo desde a ultima mensagem que
// ja temos guardada (afterId, ver ListMessagesService no backend). Guarda
// tambem mensagens de tickets que nao estao abertos no momento, pra qualquer
// conversa reabrir instantanea, nao so a que estava na tela.
import { openLocalDb, TICKETS_STORE } from "./localDb";

// Fila serial de gravacao por ticket. Sem isso, dois merges concorrentes no
// mesmo ticket (ex: a Hellen dando "Dar Baixa" em varios comprovantes
// seguidos no grupo "Extrato Ativo Pay", cada clique + o eco do socket)
// liam a MESMA base, mesclavam por cima e o ultimo a gravar apagava a baixa
// que o anterior tinha acabado de registrar — no refresh o botao verde
// "Dar Baixa" voltava, e como a sincronizacao incremental (afterId) nunca
// rebusca uma mensagem antiga que mudou, ficava preso assim. Agora cada
// gravacao daquele ticket espera a anterior TERMINAR (commit real no
// IndexedDB) antes de comecar, entao o read-modify-write vira atomico na
// pratica. (27/08/2026)
const ticketWriteQueues = new Map();

const enqueueTicketWrite = (ticketId, task) => {
  const key = String(ticketId);
  const previous = ticketWriteQueues.get(key) || Promise.resolve();
  // Resultado entregue ao chamador (pode resolver com valor ou rejeitar).
  const result = previous.then(() => task());
  // Elo da fila: NUNCA rejeita, senao uma gravacao com erro travaria todas
  // as proximas daquele ticket.
  const link = result.then(
    () => {},
    () => {}
  );
  ticketWriteQueues.set(key, link);
  link.then(() => {
    if (ticketWriteQueues.get(key) === link) {
      ticketWriteQueues.delete(key);
    }
  });
  return result;
};

const readTicketRecord = (db, ticketId) =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(TICKETS_STORE, "readonly");
      const req = tx.objectStore(TICKETS_STORE).get(String(ticketId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });

// Grava (substitui) o registro inteiro do ticket e SO resolve quando a
// transacao commitou de verdade (tx.oncomplete). Antes a funcao retornava
// assim que enfileirava o put(), entao um F5 / reload do .exe logo depois de
// clicar "Dar Baixa" abortava a transacao no meio e a alteracao se perdia.
const writeTicketRecord = (db, ticketId, messages, hasMore) =>
  new Promise((resolve) => {
    try {
      const tx = db.transaction(TICKETS_STORE, "readwrite");
      tx.objectStore(TICKETS_STORE).put({
        ticketId: String(ticketId),
        messages,
        hasMore: Boolean(hasMore),
        updatedAt: Date.now()
      });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch (e) {
      resolve(false);
    }
  });

export const getCachedTicketMessages = async (ticketId) => {
  try {
    const db = await openLocalDb();
    if (!db) return null;
    return await readTicketRecord(db, ticketId);
  } catch (e) {
    return null;
  }
};

export const putCachedTicketMessages = (ticketId, messages, hasMore) =>
  enqueueTicketWrite(ticketId, async () => {
    try {
      const db = await openLocalDb();
      if (!db) return;
      await writeTicketRecord(db, ticketId, messages, hasMore);
    } catch (e) {
      // silencioso — cache e so uma otimizacao, nunca deve quebrar o fluxo normal
    }
  });

// Mescla mensagens novas no cache de um ticket — dedupe por id, sempre
// ordenado por data. Usado tanto pela sincronizacao incremental quanto por
// mensagens chegando ao vivo pelo socket (mesmo de tickets fechados agora).
// O get + merge + put roda dentro da fila serial do ticket, entao nunca
// perde uma atualizacao concorrente.
export const mergeCachedTicketMessages = (ticketId, newMessages, hasMoreHint) => {
  if (!ticketId || !newMessages || !newMessages.length) {
    return Promise.resolve(null);
  }
  return enqueueTicketWrite(ticketId, async () => {
    try {
      const db = await openLocalDb();
      if (!db) return null;
      const existing = await readTicketRecord(db, ticketId);
      const byId = new Map(
        (existing?.messages || []).map((m) => [String(m.id), m])
      );
      newMessages.forEach((m) => {
        if (m && m.id != null) byId.set(String(m.id), m);
      });
      const merged = [...byId.values()].sort(
        (a, b) =>
          new Date(a.createdAt) - new Date(b.createdAt) ||
          String(a.id).localeCompare(String(b.id))
      );
      const hasMore =
        typeof hasMoreHint === "boolean"
          ? hasMoreHint
          : Boolean(existing?.hasMore);
      await writeTicketRecord(db, ticketId, merged, hasMore);
      return merged;
    } catch (e) {
      return null;
    }
  });
};
